import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { User } from 'firebase/auth';
import { authConfigured, loadAuth, signInWith, signOutUser, SIGNED_IN_HINT, type Provider } from './firebase';

interface AuthState {
  /** Sign-in is set up for this build. */
  enabled: boolean;
  /** We know whether someone is signed in (false until checked). */
  ready: boolean;
  user: { uid: string; name?: string; email?: string; photo?: string } | null;
  signIn: (provider: Provider) => Promise<void>;
  signOut: () => Promise<void>;
  /** A fresh ID token for API calls, or null when signed out. */
  idToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState>({
  enabled: false,
  ready: true,
  user: null,
  signIn: async () => undefined,
  signOut: async () => undefined,
  idToken: async () => null,
});

const toUser = (u: User | null): AuthState['user'] =>
  u ? { uid: u.uid, name: u.displayName || undefined, email: u.email || undefined, photo: u.photoURL || undefined } : null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabled = authConfigured();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [ready, setReady] = useState(!enabled);

  // Restore a session only for someone who signed in before: everyone else
  // never downloads the Firebase SDK.
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || !localStorage.getItem(SIGNED_IN_HINT)) {
      setReady(true);
      return;
    }
    let unsubscribe = () => undefined as void;
    loadAuth()
      .then(async auth => {
        const { onAuthStateChanged } = await import('firebase/auth');
        unsubscribe = onAuthStateChanged(auth, user => {
          setFirebaseUser(user);
          if (!user) localStorage.removeItem(SIGNED_IN_HINT);
          setReady(true);
        });
      })
      .catch(() => setReady(true));
    return () => unsubscribe();
  }, [enabled]);

  const signIn = useCallback(async (provider: Provider) => {
    const user = await signInWith(provider);
    setFirebaseUser(user);
    setReady(true);
    // Lets the backend record the account (and tell the team about a new one).
    // Fire and forget: signing in never waits on it.
    user
      ?.getIdToken()
      .then(token => fetch(`${process.env.GATSBY_API_URL || 'https://api.scamx.ai'}/api/v1/me`, { headers: { Authorization: `Bearer ${token}` } }))
      .catch(() => undefined);
  }, []);

  const signOut = useCallback(async () => {
    await signOutUser();
    setFirebaseUser(null);
  }, []);

  const idToken = useCallback(async () => (firebaseUser ? firebaseUser.getIdToken() : null), [firebaseUser]);

  const value = useMemo<AuthState>(
    () => ({ enabled, ready, user: toUser(firebaseUser), signIn, signOut, idToken }),
    [enabled, ready, firebaseUser, signIn, signOut, idToken]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
